import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";

import {
  GetCompanyAdminRewardPeriods,
  GetSpotPointsLeaders,
} from "../../../redux/constants/apiConstants";
import { fetchApi } from "../../../utils/methods";
import { getCompanyDepartment, getCompanyLocation } from "../../../redux/actions";

import SkeletonLoder from "../../common/skeletonLoder";
import SpotRewardsListView from "./SpotRewardsListView";
import SpotPointsReportModal from "../Rewards/SpotPointsReportModal";
import Waiting from "../../Waiting";

import {
  BrandingTab,
  BrandingTabContainer,
  ContainerMain,
} from "../../common/commonStyles";
import { HeaderRow } from "../../EventDetails/styles";
import { RewarDocumentsContainer } from "../Documents/styles";
import SearchInput from "../Documents/SearchInput";
import HoverButton from "../../common/HoverButton";
import AllOptionsFilterPopup from "../../common/CommonDeptLoc/AllOptionsFilterPopup";
import CommonDropdown from "../../common/CommonDropDown/CommonDropdown";

import {
  AdminDownloadIcon,
  BackArrowFeed,
  NewFilterIcon,
} from "../../../utils/icons";
import { AdminIconContainer } from "../../RecogntionAdmin/styles";

const tabs = [{ label: "Spot Awards", path: "dashboard" }];

const userType = [
  { name: "Employee", id: 1 },
  { name: "Spouse", id: 2 },
  { name: "Dependent", id: 3 },
];

const SpotPointsRewardsDashboard = ({
  companyInfo,
  brandingColor,
  locationDetails,
  departmentDetails,
  getCompanyLocation,
  getCompanyDepartment,
}) => {
  const history = useHistory();

  // ---------------- STATE ----------------
  const [loading, setLoading] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [rewardPeriods, setRewardPeriods] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    departments: [],
    locations: [],
  });

  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [ui, setUI] = useState({
    yearDropdown: false,
    filterDropdown: false,
    exportModal: false,
    showYearDropdown: true,
  });

  // ---------------- REFS ----------------
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const loaderRef = useRef(null);

  // ---------------- INIT ----------------
  useEffect(() => {
    if (!companyInfo?.id) return;

    getCompanyLocation(companyInfo.id);
    getCompanyDepartment(companyInfo.id);
    fetchRewardPeriods();
  }, [companyInfo?.id]);

  // ---------------- FETCH PERIODS ----------------
  const fetchRewardPeriods = async () => {
    setLoading(true);
    try {
      const res = await fetchApi(
        `${GetCompanyAdminRewardPeriods}?company_id=${companyInfo.id}`,
        "GET"
      );

      if (res.status !== "success") {
        return toast.error(res.message);
      }

      const periods =
        res?.data?.periods?.filter(
          (p) => dayjs(p.reward_start_date).year() >= 2026
        ) || [];

      setRewardPeriods(periods);
      setSelectedYear(periods[0] || null);

      setUI((prev) => ({
        ...prev,
        showYearDropdown: periods.length > 1,
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RESET + FETCH ----------------
  const resetAndFetch = useCallback(
    (searchValue = search) => {
      if (!selectedYear?.reward_period_id) return;

      pageRef.current = 1;
      setLeaders([]);
      setHasMore(true);

      fetchLeaders(searchValue, true);
    },
    [selectedYear, filters]
  );

  // ---------------- FETCH LEADERS ----------------
  const fetchLeaders = async (searchValue = search, isSearch = false) => {
    if (!selectedYear?.reward_period_id || loadingMore) return;
    if (!hasMore && !isSearch && pageRef.current !== 1) return;

    setLoadingMore(true);

    const payload = {
      company_id: companyInfo.id,
      reward_period_id: selectedYear.reward_period_id,
      page: pageRef.current,
      limit: 25,
      search: searchValue,
      department_ids: filters.departments,
      location_ids: filters.locations,
    };

    try {
      const res = await fetchApi(GetSpotPointsLeaders, "POST", payload);

      if (searchRef.current !== searchValue) return;

      if (res.status !== "success") {
        return toast.error(res.error);
      }

      const newData = res?.data?.users || [];

      setLeaders((prev) =>
        pageRef.current === 1 ? newData : [...prev, ...newData]
      );

      const { current_page, last_page } = res?.data?.pagination || {};

      setHasMore(current_page < last_page);
      pageRef.current += 1;
    } catch (err) {
      window.console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  // ---------------- EFFECTS ----------------

  // Year / filters change
  useEffect(() => {
    resetAndFetch();
  }, [selectedYear?.reward_period_id, filters]);

  // Debounced search
  useEffect(() => {
    window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      searchRef.current = search;
      resetAndFetch(search);
    }, 500);

    return () => window.clearTimeout(debounceRef.current);
  }, [search]);

  // Infinite scroll
  useEffect(() => {
    if (!loaderRef.current) return;
  
    const observer = new window.IntersectionObserver(
      (entries) => {
        const target = entries[0];
  
        if (
          target.isIntersecting &&
          hasMore &&
          !loadingMore
        ) {
          fetchLeaders();
        }
      },
      {
        root: null, // viewport
        rootMargin: "500px", // start early
        threshold: 0.1,
      }
    );
  
    observer.observe(loaderRef.current);
  
    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, loadingMore]);

  const updateDropdown = (title, id, /*titleKey, idKey*/) => {
    window.console.log("titiel",title);
    window.console.log("id",id)

    window.console.log("updateDropdown",id)
    // this.setState(
    //   {
    //     [titleKey]: title,
    //     [idKey]: id,
    //   },
    //   () => {
    //     this.viewUsersToAdd();
    //   }
    // );
  };

  // ---------------- UI ----------------
  return (
    <ContainerMain padding="25px 0px 0px 0px">
      <div className="content-wrap">
        {/* HEADER */}
        <BrandingTabContainer>
          <div className="wrapper backbutton">
            <AdminIconContainer onClick={() => history.goBack()}>
              {BackArrowFeed("#DA6C74")}
            </AdminIconContainer>

            {tabs.map((tab, i) => (
              <BrandingTab
                key={i}
                active
                background={brandingColor || "#0b4170"}
              >
                {tab.label}
              </BrandingTab>
            ))}
          </div>

          {ui.showYearDropdown && (
            <div style={{display:'flex'}}>
              <CommonDropdown
                tileContainerStyle={{ width: "100%", padding:"0px", margin: "0px" }}
                dropdownButtonPadding={"0px 35px 0px 12px"}
                dropdownStyle={{ top: "95px" }}
                isRequired={true}
                dropdownOpen={ui.yearDropdown}
                placeholder={"Year"}
                title={`${dayjs(selectedYear?.reward_start_date).format(
                  "MMM YYYY"
                )} - ${dayjs(selectedYear?.reward_end_date).format("MMM YYYY")}`}
                onClick={() =>
                  setUI((p) => ({ ...p, yearDropdown: !p.yearDropdown }))
                }
                data={rewardPeriods}
                active={selectedYear}
                onSelect={(val) => setSelectedYear({ ...val })}
                onSelectParameter={["year", "firstDate", "month"]}
                showDateRange
                dateRangeKeys={{
                  start: "reward_start_date",
                  end: "reward_end_date",
                }}
                dropIcon={true}
                isManagerMargin={'0px'}
                top={'8px'}
              />
            </div>
          )}
        </BrandingTabContainer>

        {/* SEARCH + FILTER */}
        <RewarDocumentsContainer>
          <SearchInput
            placeholder="Search"
            onChange={(val) => setSearch(val)}
          />

          <div className="icons-container">
            {/* <span
              onClick={() =>
                setUI((p) => ({ ...p, exportModal: true }))
              }
            >
              {AdminDownloadIcon()}
            </span> */}

            <HoverButton
              title="Download"
              svgPath={AdminDownloadIcon(20,24)}
              onClick={() =>
                setUI((p) => ({ ...p, exportModal: !p.exportModal }))
              }
              width={"20px"}
              height={"24px"}
              fillOpacity={1}
              activeColor={"#DA6C74"}
              inActiveColor={"#DA6C74"}
              backgroundColor={"#FCF2F3"}
              backgroundColorActive={"#DA6C74"}
            />

            <div
              className="compare"
              id="filterSection"
              style={{ position: "relative" }}
            >

              <HoverButton
                title="Filter"
                svgPath={<NewFilterIcon />}
                onClick={() =>
                  setUI((p) => ({
                    ...p,
                    filterDropdown: !p.filterDropdown,
                  }))
                }
                width={"22px"}
                height={"22px"}
                fillOpacity={1}
                activeColor={"#DA6C74"}
                inActiveColor={"#DA6C74"}
                backgroundColor={"#FCF2F3"}
                backgroundColorActive={"#DA6C74"}
              />
  
              <AllOptionsFilterPopup
                companyId={companyInfo?.id}
                setSelectedFilters={setFilters}
                isDropdownOpen={ui.filterDropdown}
                setIsDropdownOpen={() =>
                  setUI((p) => ({
                    ...p,
                    filterDropdown: !p.filterDropdown,
                  }))
                }
                filters={["department", "location"]}
                userType={userType}
                locationDetails={locationDetails}
                departmentDetails={departmentDetails}
                page={"active"}
                updateDropdown={updateDropdown}
              />
            </div>
          </div>
        </RewarDocumentsContainer>

        {/* CONTENT */}
        {loading ? (
          <HeaderRow style={{marginTop:'20px'}}>
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoder key={i} width="1248px" height="387px" />
            ))}
          </HeaderRow>
        ) : loadingMore && pageRef.current === 1 ? (
          <Waiting />
        ) : (
          <div>
            <SpotRewardsListView leaders={leaders} loadingMore={loadingMore} />
          
            {/* 👇 Observer target */}
            <div ref={loaderRef} style={{ height: "20px" }} />
          
            {loadingMore && <Waiting />}
          </div>
        )}
      </div>

      {/* MODAL */}
      {ui.exportModal && (
        <SpotPointsReportModal
          selectedYear={selectedYear}
          companyInfo={companyInfo}
          departmentDetails={departmentDetails}
          locationDetails={locationDetails}
          show={ui.exportModal}
          onCancel={() =>
            setUI((p) => ({ ...p, exportModal: false }))
          }
        />
      )}
    </ContainerMain>
  );
};

// ---------------- PROPS ----------------
SpotPointsRewardsDashboard.propTypes = {
  companyInfo: PropTypes.object,
  brandingColor: PropTypes.string,
  locationDetails: PropTypes.array,
  departmentDetails: PropTypes.array,
  getCompanyDepartment: PropTypes.func,
  getCompanyLocation: PropTypes.func,
};

const mapStateToProps = (state) => ({
  brandingColor: state.wellnessDashboard.brandingColor,
  locationDetails: state.register.locationDetails,
  departmentDetails: state.register.departmentDetails,
});

export default connect(mapStateToProps, {
  getCompanyDepartment,
  getCompanyLocation,
})(SpotPointsRewardsDashboard);